/**
 * Phase 2.4 — Parking-to-bay timer-start confirmation foundation.
 *
 * Doctrine:
 *   - Bucket 3 §3.4.3 Scheduled-but-Parked Ticket
 *   - Bucket 3 §3.4.6 Held-in-Parking Ticket
 *   - Bucket 3 §3.8.1 Parking Spot to Bay/Lane Transition
 *   - Bucket 3 §3.8.2 Scheduled-but-Parked Pull-In
 *   - Bucket 3 §3.8.3 Held-in-Parking Return to Bay
 *
 * Why this module exists
 * ----------------------
 * Bucket 3 §3.8.1 requires bay/lane confirmation when a timer starts on a
 * vehicle physically located in a parking spot or the general parking lot.
 * Today the canonical timer-start path (`startTicketTimer` /
 * `mutateTicketTimerState`) reads the ticket's existing `bay_id` and uses
 * `hasBayAssigned = !!bay_id` to decide whether to clear parking fields.
 * That branch handles both cases mechanically, but there is NO existing UI
 * or server gate that prompts the user to pick a bay before a parked
 * ticket's timer starts — a parked ticket can start a timer with
 * `bay_id=null` and the vehicle keeps its parking fields, leaving the
 * production state ambiguous.
 *
 * This module gives callers (timer-start UI affordances, server-side
 * mutation paths, board action surfaces) a single canonical answer to:
 *
 *   "Can this ticket start a timer right now without prompting the user to
 *    pick a bay? If not, why not?"
 *
 * Phase 2.4 scope: HELPERS + TESTS ONLY.
 *   - Additive. Does NOT modify `mutateTicketTimerState`, `move_vehicle_
 *     location` RPC, any timer-start writer, cascade, AutoBump, OptiAssign,
 *     or Due Out enforcement.
 *   - Does NOT alter Tech OptiTime, Start Time locking semantics, or
 *     physical-location writes anywhere.
 *   - No DB enum, no migration, no new column.
 *   - No pause/resume mutation behavior changes.
 *   - No new UI confirmation flow is added — `CheckInVehicleDialog` is for
 *     the OPPOSITE direction (vehicle-arrives → assigned to parking spot)
 *     and is NOT a fit. A future slice may add a "Confirm Bay for Pull-In"
 *     dialog that consumes this helper.
 *
 * Builds on:
 *   - Phase 1.1 `getProductionState` — canonical state derivation.
 *   - Phase 1.3 `isHeldInParking` / `isBayBlockingProductionState` /
 *     `isProductionRelevantState` — operational property predicates.
 */

import {
  getProductionState,
  isBayBlockingProductionState,
  type ProductionState,
  type ProductionStateTicketShape,
} from './ticketProductionState';

// =============================================================================
// Location state (where is the vehicle right now?)
// =============================================================================

/**
 * The four mutually-exclusive locations a ticket's vehicle can be at the
 * timer-start moment. Mirrors the physical-location triad
 * (`bay_id` / `parking_spot_id` / `parking_lot`) used by
 * `parkingLotState.isPhysicallyLocated` and the `sync_vehicle_present`
 * trigger.
 */
export type TimerStartLocationState =
  | 'in_bay' // bay_id set → in a bay/lane right now
  | 'in_parking_spot' // parking_spot_id set, no bay_id → specific parking spot
  | 'in_general_lot' // parking_lot=true, no spot, no bay → general lot
  | 'no_location'; // no physical-location signal at all

/**
 * Resolve the current physical-location state for the timer-start moment.
 *
 * Precedence (first match wins, mirroring the canonical write paths in
 * `mutateTicketTimerState` and `move_vehicle_location` RPC):
 *   1. `bay_id != null`                 → 'in_bay'
 *   2. `parking_spot_id != null`        → 'in_parking_spot'
 *   3. `parking_lot === true`           → 'in_general_lot'
 *   4. otherwise                        → 'no_location'
 *
 * Pure: depends only on the ticket fields, never reads the clock or DB.
 */
export function getTimerStartLocationState(
  ticket: ProductionStateTicketShape,
): TimerStartLocationState {
  if (ticket.bay_id != null) return 'in_bay';
  if (ticket.parking_spot_id != null) return 'in_parking_spot';
  if (ticket.parking_lot === true) return 'in_general_lot';
  return 'no_location';
}

// =============================================================================
// Boolean predicates
// =============================================================================

/**
 * Is this ticket physically parked AND lacking a bay assignment at the
 * timer-start moment?
 *
 * Returns true for the two parking-but-no-bay states:
 *   - 'in_parking_spot' — vehicle in a specific spot
 *   - 'in_general_lot'  — vehicle in the general lot
 *
 * Returns false for:
 *   - 'in_bay'        — vehicle already in a bay (no parking-to-bay needed)
 *   - 'no_location'   — no physical-location signal (separate concern,
 *                       handled by the Bucket 2 §2.11.3 timer-lock rules)
 */
export function isParkedBeforeTimerStart(
  ticket: ProductionStateTicketShape,
): boolean {
  const loc = getTimerStartLocationState(ticket);
  return loc === 'in_parking_spot' || loc === 'in_general_lot';
}

/**
 * Does this ticket require an explicit bay/lane confirmation step before
 * its timer can start?
 *
 * Returns TRUE if and only if:
 *   - The ticket is physically parked (in a spot or general lot), AND
 *   - The production state is active-production-relevant in a way that
 *     the parking-to-bay pull-in path applies:
 *       - `scheduled_but_parked` (Bucket 3 §3.8.2 pull-in), OR
 *       - `held_in_parking`      (Bucket 3 §3.8.3 return to bay).
 *
 * Returns FALSE for:
 *   - 'in_bay' states (already in a bay).
 *   - Terminal states (deleted/archived/picked_up/completed_awaiting_pickup)
 *     where the timer should not start through normal active-production
 *     mutations at all — those are handled by Phase 1.4's
 *     `canPerformActiveProductionMutation` gate.
 *   - `parking_lot_inactive` / `estimate_prospect` — these need their own
 *     reintroduction / conversion workflow before bay confirmation
 *     becomes the question.
 */
export function requiresBayConfirmationBeforeTimerStart(
  ticket: ProductionStateTicketShape,
): boolean {
  if (!isParkedBeforeTimerStart(ticket)) return false;
  const state = getProductionState(ticket);
  return state === 'scheduled_but_parked' || state === 'held_in_parking';
}

/**
 * Can this ticket's timer start right now without prompting for a bay?
 *
 * Returns TRUE for active-production-relevant states that already have a
 * bay assigned (or where the active-production state itself implies a
 * bay):
 *   - `active_in_bay`   — vehicle in bay, already running or actively
 *                         started.
 *   - `held_in_bay`     — vehicle in bay, paused/held.
 *   - `scheduled` AND `bay_id != null` — scheduled, bay-assigned, not
 *     parked.
 *
 * Returns FALSE for:
 *   - Any parked-and-needs-bay state (would trigger bay confirmation).
 *   - Any terminal state (would block at the active-production-mutation
 *     gate, not the bay-confirmation gate).
 *   - Any inactive state (would require its own reintroduction /
 *     conversion workflow first).
 *   - `scheduled` with no bay_id AND no parking signal — uncommon row;
 *     resolves to FALSE because there is no bay to start in. The Bucket
 *     2 §2.11.3 timer-lock rule covers this path.
 */
export function canStartTimerWithoutBayConfirmation(
  ticket: ProductionStateTicketShape,
): boolean {
  const state = getProductionState(ticket);
  const loc = getTimerStartLocationState(ticket);
  if (isBayBlockingProductionState(state)) {
    // active_in_bay / held_in_bay — bay is by definition in use.
    return loc === 'in_bay';
  }
  if (state === 'scheduled') {
    // Scheduled tickets need a bay assigned and no parking signal.
    return loc === 'in_bay';
  }
  return false;
}

// =============================================================================
// Combined requirement result
// =============================================================================

/**
 * What kind of timer-start the caller is facing for this ticket.
 *
 *   - 'no_confirmation_needed'    — ready to start (bay already known and
 *                                   the ticket is in a startable state).
 *   - 'bay_confirmation_required' — parked active-production ticket. The
 *                                   caller MUST surface a bay/lane picker
 *                                   before invoking `startTicketTimer`.
 *   - 'blocked_terminal_state'    — deleted / archived / picked_up /
 *                                   completed_awaiting_pickup. Cannot
 *                                   start a timer through normal active
 *                                   production. Use the appropriate
 *                                   correction workflow (Phase 1.4).
 *   - 'blocked_inactive_state'    — parking_lot_inactive / estimate_
 *                                   prospect. Requires reintroduction or
 *                                   conversion to active scheduling first.
 *   - 'blocked_no_bay_no_parking' — `scheduled` with no bay_id AND no
 *                                   parking signal. Bucket 2 §2.11.3
 *                                   timer-lock applies. (Surfaced as its
 *                                   own kind so callers can distinguish
 *                                   from terminal/inactive blockers.)
 */
export type TimerStartRequirementKind =
  | 'no_confirmation_needed'
  | 'bay_confirmation_required'
  | 'blocked_terminal_state'
  | 'blocked_inactive_state'
  | 'blocked_no_bay_no_parking';

export interface TimerStartRequirement {
  /** What the caller needs to do (or refuse) for this timer-start attempt. */
  kind: TimerStartRequirementKind;
  /** Canonical production state at the moment of the question. */
  productionState: ProductionState;
  /** Current physical-location state at the moment of the question. */
  locationState: TimerStartLocationState;
  /** Short audit-friendly explanation of why this kind resolved. */
  reason: string;
}

/**
 * One-shot computation for the parking-to-bay timer-start gate. Use this
 * when the caller needs all four facts together — e.g., a timer-start
 * affordance that wants to show different copy / dialogs per kind.
 *
 * Resolution order:
 *   1. Terminal states (deleted / archived / picked_up / completed_
 *      awaiting_pickup) → `blocked_terminal_state`.
 *   2. Inactive states (parking_lot_inactive / estimate_prospect) →
 *      `blocked_inactive_state`.
 *   3. Parked active-production states (scheduled_but_parked /
 *      held_in_parking) AND not in bay → `bay_confirmation_required`.
 *   4. Bay-blocking states (active_in_bay / held_in_bay) AND in bay →
 *      `no_confirmation_needed`.
 *   5. Scheduled AND in bay → `no_confirmation_needed`.
 *   6. Scheduled AND no bay/parking signal → `blocked_no_bay_no_parking`.
 *   7. Defensive fallback: `blocked_inactive_state` (catches any future
 *      state that isn't enumerated yet).
 */
export function getParkingToBayTimerStartRequirement(
  ticket: ProductionStateTicketShape,
): TimerStartRequirement {
  const productionState = getProductionState(ticket);
  const locationState = getTimerStartLocationState(ticket);

  // 1. Terminal states.
  if (
    productionState === 'deleted' ||
    productionState === 'archived' ||
    productionState === 'picked_up' ||
    productionState === 'completed_awaiting_pickup'
  ) {
    return {
      kind: 'blocked_terminal_state',
      productionState,
      locationState,
      reason: `Terminal state '${productionState}' — cannot start timer through normal active production.`,
    };
  }

  // 2. Inactive states (different correction workflow required first).
  if (
    productionState === 'parking_lot_inactive' ||
    productionState === 'estimate_prospect'
  ) {
    return {
      kind: 'blocked_inactive_state',
      productionState,
      locationState,
      reason:
        productionState === 'parking_lot_inactive'
          ? 'Parking-lot-inactive — reintroduce to active scheduling before starting a timer.'
          : 'Estimate/prospect — convert to scheduled work before starting a timer.',
    };
  }

  // 3. Parked active-production states need bay confirmation.
  if (
    productionState === 'scheduled_but_parked' ||
    productionState === 'held_in_parking'
  ) {
    return {
      kind: 'bay_confirmation_required',
      productionState,
      locationState,
      reason: `${productionState} — caller must confirm bay/lane before timer start.`,
    };
  }

  // 4–5. Bay-blocking AND scheduled-in-bay states.
  if (locationState === 'in_bay') {
    if (
      productionState === 'active_in_bay' ||
      productionState === 'held_in_bay' ||
      productionState === 'scheduled'
    ) {
      return {
        kind: 'no_confirmation_needed',
        productionState,
        locationState,
        reason: `${productionState} in bay — timer can start without confirmation.`,
      };
    }
  }

  // 6. Scheduled with no bay AND no parking signal.
  if (productionState === 'scheduled') {
    return {
      kind: 'blocked_no_bay_no_parking',
      productionState,
      locationState,
      reason:
        'Scheduled ticket has no bay and no parking signal — vehicle location must be confirmed first (Bucket 2 §2.11.3 timer-lock).',
    };
  }

  // 7. Defensive fallback. Should be unreachable today; guards future
  //    additions to ProductionState that aren't routed above.
  return {
    kind: 'blocked_inactive_state',
    productionState,
    locationState,
    reason: `Unrecognised production state '${productionState}' — refusing timer start.`,
  };
}
